import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const empty = { employee_id: '', name: '', email: '', password: '', role: 'employee', department: '', designation: '', phone: '' };

export default function Users() {
  const [users, setUsers]       = useState([]);
  const [modal, setModal]       = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(empty);
  const [search, setSearch]     = useState('');
  const [roleFilter, setRoleFilter] = useState('employee');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');

  const load = () => {
    setLoading(true);
    api.get('/users')
      .then(r => setUsers(r.data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditing(null); setModal(true); setMsg(''); };
  const openEdit   = (u) => { setForm({ ...u, password: '' }); setEditing(u.id); setModal(true); setMsg(''); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      if (editing) { await api.put(`/users/${editing}`, form); }
      else         { await api.post('/users', form); }
      setModal(false);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const filtered = users.filter(u => {
    const matchRole   = roleFilter === 'all' || u.role === roleFilter;
    const q           = search.toLowerCase();
    const matchSearch = !q || u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.employee_id || '').toLowerCase().includes(q);
    return matchRole && matchSearch;
  });

  const counts = {
    all:        users.length,
    employee:   users.filter(u => u.role === 'employee').length,
    supervisor: users.filter(u => u.role === 'supervisor').length,
    synced:     users.filter(u => u.cp_employee_id).length,
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 className="fw-bold mb-0">User Management</h5>
          <small className="text-muted">
            {counts.employee} employees · {counts.supervisor} admins ·
            <span className="text-success fw-semibold"> {counts.synced} synced from CP</span>
          </small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <input
            className="form-control form-control-sm"
            placeholder="Search name, email, ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 210 }}
          />
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            <i className="bi bi-person-plus me-1" />Add User
          </button>
        </div>
      </div>

      {/* Role filter tabs */}
      <ul className="nav nav-tabs mb-3" style={{ fontSize: '.85rem' }}>
        {[
          { key: 'employee',   label: `Employees (${counts.employee})` },
          { key: 'supervisor', label: `Admins (${counts.supervisor})` },
          { key: 'all',        label: `All (${counts.all})` },
        ].map(t => (
          <li className="nav-item" key={t.key}>
            <button
              className={`nav-link py-1 px-3 ${roleFilter === t.key ? 'active fw-semibold' : ''}`}
              onClick={() => setRoleFilter(t.key)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : (
        <div className="table-card">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Email</th>
                <th>Department / Designation</th>
                <th>Role</th>
                <th>Source</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted py-4">No users found</td></tr>
              ) : filtered.map(u => (
                <tr key={u.id}>
                  {/* Avatar + name */}
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold"
                        style={{
                          width: 36, height: 36, fontSize: '.85rem', flexShrink: 0,
                          background: u.cp_employee_id ? '#0d6efd' : '#6c757d',
                        }}
                      >
                        {(u.name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-semibold small">{u.name}</div>
                        <div className="text-muted" style={{ fontSize: '.72rem' }}>{u.employee_id}</div>
                      </div>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="small text-muted">{u.email}</td>

                  {/* Dept / Designation */}
                  <td className="small">
                    <div>{u.department || <span className="text-muted">—</span>}</div>
                    {u.designation && <div className="text-muted" style={{ fontSize: '.72rem' }}>{u.designation}</div>}
                  </td>

                  {/* Role */}
                  <td>
                    <span className={`badge ${u.role === 'supervisor' ? 'bg-warning text-dark' : 'bg-primary-subtle text-primary'}`}>
                      {u.role === 'supervisor' ? 'Admin' : 'Employee'}
                    </span>
                  </td>

                  {/* Source (CP sync or manual) */}
                  <td>
                    {u.cp_employee_id ? (
                      <span title={`Synced from Compliance Portal on ${fmtDate(u.cp_synced_at)}`}>
                        <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '.7rem' }}>
                          <i className="bi bi-arrow-repeat me-1" />CP Synced
                        </span>
                        {u.cp_synced_at && (
                          <div className="text-muted" style={{ fontSize: '.68rem' }}>{fmtDate(u.cp_synced_at)}</div>
                        )}
                      </span>
                    ) : (
                      <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle" style={{ fontSize: '.7rem' }}>
                        Manual
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td>
                    <span className={`badge ${u.is_active ? 'bg-success' : 'bg-secondary'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-outline-primary btn-sm py-0 px-2" onClick={() => openEdit(u)} title="Edit">
                        <i className="bi bi-pencil" />
                      </button>
                      {u.is_active && (
                        <button className="btn btn-outline-danger btn-sm py-0 px-2" onClick={() => deactivate(u.id)} title="Deactivate">
                          <i className="bi bi-person-x" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Edit User' : 'Add User'}</h5>
                <button className="btn-close" onClick={() => setModal(false)} />
              </div>
              <form onSubmit={save}>
                <div className="modal-body">
                  {msg && <div className="alert alert-danger py-2 small">{msg}</div>}
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Employee ID *</label>
                      <input className="form-control" value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} required />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Full Name *</label>
                      <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Email *</label>
                      <input type="email" className="form-control" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">{editing ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                      <input type="password" className="form-control" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editing} minLength={6} />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Role</label>
                      <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                        <option value="employee">Employee</option>
                        <option value="supervisor">Admin</option>
                      </select>
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Phone</label>
                      <input className="form-control" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Department</label>
                      <input className="form-control" value={form.department || ''} onChange={e => setForm({ ...form, department: e.target.value })} />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold">Designation</label>
                      <input className="form-control" value={form.designation || ''} onChange={e => setForm({ ...form, designation: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
